<template>
  <div>
    <div class="wz-page-header">
      <div class="flex items-center justify-between">
        <h1 class="wz-page-title mb-0">Pagos Operativos</h1>
      </div>
    </div>

    <wz-table-container>
      <wz-table-toolbar>
        <template #left>
          <div class="flex flex-wrap items-end" style="gap: 8px;">
            <wz-select v-model="filters.companyId" :items="companyOptions" defaultOption="Todas las empresas" class="op-filter" />
            <wz-select v-model="filters.conceptId" :items="conceptOptions" defaultOption="Todos los conceptos" class="op-filter" />
            <wz-select v-model="filters.year" :items="yearOptions" defaultOption="Año" class="op-filter-sm" />
            <wz-select v-model="filters.month" :items="monthOptions" defaultOption="Mes" class="op-filter-sm" />
            <wz-select v-model="filters.status" :items="statusOptions" defaultOption="Todos los estados" class="op-filter" />
            <wz-button variant="outline" @click="loadData">Filtrar</wz-button>
            <wz-button variant="light" @click="clearFilters">Limpiar</wz-button>
          </div>
        </template>
        <template #right>
          <wz-button variant="primary" @click="openNewBatch">Nuevo lote</wz-button>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th>#</th>
            <th>Empresa</th>
            <th>Concepto</th>
            <th>Período</th>
            <th class="text-right">Total (S/)</th>
            <th class="text-center">Registros</th>
            <th>Estado</th>
            <th class="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="list.length === 0">
            <td colspan="8" class="text-center text-muted py-4">No se encontraron lotes.</td>
          </tr>
          <tr v-for="o in list" :key="o.id">
            <td>{{ o.id }}</td>
            <td>{{ o.companyName }}</td>
            <td>{{ o.conceptName }}</td>
            <td>{{ String(o.periodMonth).padStart(2, '0') }}/{{ o.periodYear }}</td>
            <td class="text-right">{{ formatAmount(o.totalAmount) }}</td>
            <td class="text-center">{{ o.totalRecords || 0 }}</td>
            <td><wz-badge :variant="statusVariant(o.status)" pill>{{ o.status }}</wz-badge></td>
            <td class="text-center">
              <wz-button variant="outline" size="sm" @click="$router.push({ name: 'operational-payment-detail', params: { id: o.id } })">
                <i class="ri-eye-line mr-1"></i>Ver
              </wz-button>
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <span>{{ list.length }} lote(s)</span>
      </wz-table-footer>
    </wz-table-container>

    <!-- Modal nuevo lote -->
    <wz-modal v-model="modalNew" title="Nuevo lote de pago operativo">
      <div class="mb-3">
        <label class="font-medium">Empresa <span class="text-danger">*</span></label>
        <wz-select v-model="form.companyId" :items="companyOptions" defaultOption="Seleccionar empresa" />
      </div>
      <div class="mb-3">
        <label class="font-medium">Concepto <span class="text-danger">*</span></label>
        <wz-select v-model="form.conceptId" :items="conceptOptions" defaultOption="Seleccionar concepto" />
      </div>
      <div class="flex -mx-2">
        <div class="w-1/2 px-2 mb-3">
          <label class="font-medium">Año <span class="text-danger">*</span></label>
          <wz-select v-model="form.periodYear" :items="yearOptions" defaultOption="Año" />
        </div>
        <div class="w-1/2 px-2 mb-3">
          <label class="font-medium">Mes <span class="text-danger">*</span></label>
          <wz-select v-model="form.periodMonth" :items="monthOptions" defaultOption="Mes" />
        </div>
      </div>
      <div class="mb-3">
        <label class="font-medium">Descripción</label>
        <wz-textarea v-model="form.description" :rows="2" maxlength="500" placeholder="Opcional" />
      </div>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalNew = false">Cancelar</wz-button>
        <wz-button variant="primary" :disabled="!canCreate || creating" @click="createBatch">
          {{ creating ? 'Creando...' : 'Crear' }}
        </wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import { CompanyService, OperationalPaymentConceptService, OperationalPaymentService } from '@/core/services'

export default {
  data() {
    return {
      list: [],
      companies: [],
      concepts: [],
      filters: { companyId: null, conceptId: null, year: null, month: null, status: null },
      modalNew: false,
      creating: false,
      form: { companyId: null, conceptId: null, periodYear: new Date().getFullYear(), periodMonth: new Date().getMonth() + 1, description: null },
    }
  },
  computed: {
    companyOptions() { return this.companies.map(c => ({ key: c.id, label: c.name })) },
    conceptOptions() { return this.concepts.map(c => ({ key: c.id, label: c.name })) },
    statusOptions() {
      return ['PENDIENTE', 'APROBADO', 'PAGADO', 'ANULADO'].map(s => ({ key: s, label: s }))
    },
    yearOptions() {
      const y = new Date().getFullYear()
      return [y + 1, y, y - 1, y - 2].map(v => ({ key: v, label: String(v) }))
    },
    monthOptions() {
      return Array.from({ length: 12 }, (_, i) => ({ key: i + 1, label: String(i + 1).padStart(2, '0') }))
    },
    canCreate() {
      return this.form.companyId && this.form.conceptId && this.form.periodYear && this.form.periodMonth
    },
  },
  methods: {
    statusVariant(s) {
      return { PENDIENTE: 'warning-solid', APROBADO: 'info-solid', PAGADO: 'success-solid', ANULADO: 'danger-solid' }[s] || 'dark'
    },
    formatAmount(v) {
      if (v == null) return '0.00'
      return Number(v).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const params = {}
        Object.keys(this.filters).forEach(k => { if (this.filters[k] != null && this.filters[k] !== '') params[k] = this.filters[k] })
        const resp = await OperationalPaymentService.listBatches(params)
        this.list = resp.payload || []
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar los lotes')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    clearFilters() {
      this.filters = { companyId: null, conceptId: null, year: null, month: null, status: null }
      this.loadData()
    },
    async loadCatalogs() {
      try {
        this.companies = (await CompanyService.listAll()).payload || []
        this.concepts = ((await OperationalPaymentConceptService.listByStatus(1)).payload || [])
      } catch (e) { console.error(e) }
    },
    openNewBatch() {
      this.form = { companyId: null, conceptId: null, periodYear: new Date().getFullYear(), periodMonth: new Date().getMonth() + 1, description: null }
      this.modalNew = true
    },
    async createBatch() {
      if (!this.canCreate) return
      try {
        this.creating = true
        const resp = await OperationalPaymentService.createBatch(this.form)
        this.modalNew = false
        Alert.success('Lote creado')
        this.$router.push({ name: 'operational-payment-detail', params: { id: resp.payload.id } })
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al crear el lote')
      } finally {
        this.creating = false
      }
    },
  },
  async created() {
    await this.loadCatalogs()
    await this.loadData()
  },
}
</script>

<style lang="stylus" scoped>
.op-filter
  min-width 180px
.op-filter-sm
  min-width 90px
</style>
